import { useEffect, useRef } from 'react';
import { baseURL } from '../functions/httpClient';
import { heartbeat } from '../services/auth.service';

const IDLE_THRESHOLD = 30 * 60 * 1000; // 30 minutes
/**
 * Hook to manage session heartbeat tracking
 * Sends periodic heartbeat signals to keep session alive
 * and handles session cleanup on browser close
 */
export const useSessionHeartbeat = () => {
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    const updateActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(e => window.addEventListener(e, updateActivity));

    const sendHeartbeat = async () => {
      const idleTime = Date.now() - lastActivityRef.current;
      if (idleTime >= IDLE_THRESHOLD) return; // user is idle, skip

      try {
        await heartbeat();
      } catch (error) {
        console.error('Session heartbeat failed:', error);
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Set up interval to send heartbeat every 60 seconds
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 60000);

    // Handle browser close/refresh - send final heartbeat
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery during page unload
      // This is more reliable than fetch during unload events
      const token = localStorage.getItem('token');
      if (token) {
        navigator.sendBeacon(
          `${baseURL}/session/end`,
          new Blob([JSON.stringify({ endReason: 'browser_close' })], {
            type: 'application/json',
          })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      // Clear the heartbeat interval
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      events.forEach(e => window.removeEventListener(e, updateActivity));
    };
  }, []); // Empty dependency array - only run once on mount
};